'use client';
/*
 * Documentation:
 * Dropdown Menu — https://app.subframe.com/6fc7f5fbb098/library?component=Dropdown+Menu_99951515-459b-4286-919e-a89e7549b43b
 */

import React from 'react';
import { FeatherStar } from '@subframe/core';
import * as SubframeCore from '@subframe/core';
import * as SubframeUtils from '../utils';

interface DropdownDividerProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
}

const DropdownDivider = React.forwardRef<HTMLDivElement, DropdownDividerProps>(
  function DropdownDivider({ className, ...otherProps }: DropdownDividerProps, ref) {
    return (
      <div
        className={SubframeUtils.twClassNames('flex w-full items-start gap-2 px-1 py-1', className)}
        ref={ref}
        {...otherProps}
      >
        <div className="flex h-px shrink-0 grow basis-0 flex-col items-center gap-2 bg-neutral-200" />
      </div>
    );
  },
);

interface DropdownItemProps extends React.ComponentProps<typeof SubframeCore.DropdownMenu.Item> {
  children?: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
}

const DropdownItem = React.forwardRef<HTMLDivElement, DropdownItemProps>(function DropdownItem(
  { children, icon = <FeatherStar />, className, ...otherProps }: DropdownItemProps,
  ref,
) {
  return (
    <SubframeCore.DropdownMenu.Item asChild={true} {...otherProps}>
      <div
        className={SubframeUtils.twClassNames(
          'group/adcae8d6 flex h-8 w-full cursor-pointer items-center gap-2 rounded-[8px] px-3 hover:bg-neutral-100 active:bg-neutral-50 data-[highlighted]:bg-neutral-100',
          'data-[disabled]:pointer-events-none data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50 data-[disabled]:hover:bg-transparent data-[disabled]:active:bg-transparent',
          className,
        )}
        ref={ref}
      >
        {icon ? (
          <SubframeCore.IconWrapper className="text-body font-body text-default-font">
            {icon}
          </SubframeCore.IconWrapper>
        ) : null}
        {children ? (
          <span className="text-body font-body text-default-font group-hover/adcae8d6:text-default-font line-clamp-1 shrink-0 grow basis-0">
            {children}
          </span>
        ) : null}
      </div>
    </SubframeCore.DropdownMenu.Item>
  );
});

interface DropdownMenuRootProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  variant?: 'editar-01' | 'criar-01';
  className?: string;
}

const DropdownMenuRoot = React.forwardRef<HTMLDivElement, DropdownMenuRootProps>(
  function DropdownMenuRoot(
    { children, variant = 'editar-01', className, ...otherProps }: DropdownMenuRootProps,
    ref,
  ) {
    return children ? (
      <div
        className={SubframeUtils.twClassNames(
          'group/99951515 bg-default-background flex min-w-48 flex-col items-start gap-2 rounded-[16px] border border-solid border-neutral-200 px-2 py-2 shadow-lg',
          { 'flex-col flex-nowrap gap-2': variant === 'criar-01' },
          className,
        )}
        ref={ref}
        {...otherProps}
      >
        {children}
      </div>
    ) : null;
  },
);

export const DropdownMenu = Object.assign(DropdownMenuRoot, {
  DropdownDivider,
  DropdownItem,
});
